import React, {
  FC,
  PropsWithChildren,
  createContext,
  useContext,
  useMemo,
} from 'react';

import vaultFactories from '@config/vaults/vaultFactories.data';

import { FarmDto, VaultFactoryDto } from '@dto/vaults/vault-factory.dto';

export type VaultFactoriesContextType = {
  isLoading: boolean;
  vaultFactories: VaultFactoryDto[];
  vaultFactoriesFarmsMap: Record<number, Record<number, FarmDto>>;
  vaultFactoriesById: Record<number, VaultFactoryDto>;
};

export const VaultFactoriesContext = createContext<VaultFactoriesContextType>({
  vaultFactories: [],
  vaultFactoriesFarmsMap: {},
  vaultFactoriesById: {},
  isLoading: false,
});

export const useFactories = (): VaultFactoriesContextType =>
  useContext(VaultFactoriesContext);

const VaultFactoriesContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const data = vaultFactories;

  const vaultFactoriesFarmsMap = useMemo(() => {
    if (!data) return {};

    return data.reduce((acc, item) => {
      const factoryFarms = item.farms.reduce(
        (accumulator, farm) => ({
          ...accumulator,
          [farm.id]: farm,
        }),
        {},
      );
      return {
        ...acc,
        [item.id]: factoryFarms,
      };
    }, {});
  }, [data]);

  const vaultFactoriesById = useMemo(() => {
    if (!data) return {};
    return data.reduce(
      (acc, item) => ({
        ...acc,
        [item.id]: item,
      }),
      {},
    );
  }, [data]);

  return (
    <VaultFactoriesContext.Provider
      value={{
        vaultFactories: data || [],
        vaultFactoriesFarmsMap,
        vaultFactoriesById,
        isLoading: false,
      }}
    >
      {children}
    </VaultFactoriesContext.Provider>
  );
};

export default VaultFactoriesContextProvider;
